/**
 * EvaluationNormalization.jsx — Phase 2C C4: Normalization Management
 *
 * Acceptance rules enforced:
 *   1. Hidden indicator when normalization_enabled = FALSE — page shows a clear
 *      "Normalization is disabled in policy" message, NOT a hard error or implication of mandatory.
 *   2. When enabled: list of all normalization_runs with status, algorithm, input/output stats.
 *   3. Frozen population: each run shows its members (cases, input marks, output marks).
 *   4. Method/parameters shown per run (algorithm, normalization_scope).
 *   5. Approve / Reject actions available only for PENDING runs.
 *   6. Reject requires a reason (immutable decision history).
 *   7. After approve/reject the run is locked (immutable). Decision is persisted.
 *   8. Create new run: requires algorithm selection + confirmation. Available only when enabled.
 *   9. Student access blocked via EvaluationShell.
 *  10. Coordinator / normalization-authority roles only.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  TrendingUp,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  X,
  Lock,
  ChevronDown,
  ChevronUp,
  Plus,
  Info,
  BarChart3,
  Ban,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OFFERING_ID = "2c000040-2c00-2c00-2c00-2c0000000040";
const COMPONENT_ID = "2c000014-2c00-2c00-2c00-2c0000000014";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(body?.detail?.message || String(body?.detail || r.statusText)), { status: r.status, detail: body?.detail });
  return body;
}

const ALGORITHMS = [
  {
    value: "Z_SCORE",
    label: "Z-Score Standardization",
    desc: "Transforms marks to have mean 0 and standard deviation 1, then scales to the target range.",
  },
  {
    value: "LINEAR_SCALING",
    label: "Linear Scaling",
    desc: "Linearly maps the observed mark range to a target range [min, max].",
  },
  {
    value: "CUSTOM",
    label: "Custom Algorithm",
    desc: "Institution-defined algorithm applied via external configuration.",
  },
];

const STATUS_STYLE = {
  PENDING:  { cls: "bg-amber-950 text-amber-300 border-amber-800/60",  label: "Pending Approval" },
  APPROVED: { cls: "bg-teal-950 text-teal-300 border-teal-800/60",    label: "Approved" },
  REJECTED: { cls: "bg-red-950 text-red-300 border-red-800/60",       label: "Rejected" },
};

// ─── Small stat card ──────────────────────────────────────────────────────────

function StatCard({ label, value, note }) {
  return (
    <div className="bg-slate-800/60 rounded-lg px-3 py-2 text-center min-w-[80px]">
      <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm font-mono font-bold text-slate-200">{value ?? "—"}</div>
      {note && <div className="text-[9px] text-slate-600">{note}</div>}
    </div>
  );
}

// ─── Member table ─────────────────────────────────────────────────────────────

function MembersTable({ members }) {
  if (!members || members.length === 0) return (
    <div className="text-xs text-slate-600 italic py-4 text-center">No members in this run snapshot.</div>
  );
  return (
    <div className="rounded-xl border border-slate-700/60 overflow-hidden">
      <div className="grid grid-cols-[1fr_80px_80px_60px] gap-2 px-4 py-2 bg-slate-800/50 text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
        <span>Case</span>
        <span className="text-center">Input Mark</span>
        <span className="text-center">Output Mark</span>
        <span className="text-center">Max</span>
      </div>
      {members.map((m, i) => (
        <div
          key={m.id}
          data-testid={`norm-member-${i}`}
          className="grid grid-cols-[1fr_80px_80px_60px] gap-2 px-4 py-2.5 border-b border-slate-800/50 items-center last:border-b-0"
        >
          <span className="text-xs text-slate-400 font-mono">{m.evaluation_case_id?.slice(0, 16)}…</span>
          <span className="text-center text-xs font-mono font-semibold text-sky-300">{m.input_mark}</span>
          <span className={`text-center text-xs font-mono font-semibold ${m.output_mark != null ? "text-teal-300" : "text-slate-600"}`}>
            {m.output_mark ?? "—"}
          </span>
          <span className="text-center text-xs text-slate-500">{m.max_mark_snapshot}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Run card ─────────────────────────────────────────────────────────────────

function RunCard({ run, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const stl = STATUS_STYLE[run.status] || STATUS_STYLE.PENDING;
  const inputStats = run.input_stats || {};
  const outputStats = run.output_stats || {};

  return (
    <div data-testid={`norm-run-${run.id}`} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
          {run.status === "APPROVED" ? (
            <CheckCircle2 className="w-4 h-4 text-teal-400" />
          ) : run.status === "REJECTED" ? (
            <Ban className="w-4 h-4 text-red-400" />
          ) : (
            <BarChart3 className="w-4 h-4 text-amber-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${stl.cls}`}>
              {stl.label}
            </span>
            <span className="text-xs font-medium text-slate-300">
              {ALGORITHMS.find((a) => a.value === run.algorithm)?.label || run.algorithm}
            </span>
            <span className="text-[10px] text-slate-600">scope: {run.normalization_scope}</span>
          </div>
          <div className="text-[10px] text-slate-600 mt-0.5 font-mono">
            {new Date(run.created_at).toLocaleString()} · {run.members?.length ?? 0} cases
          </div>
        </div>

        {run.status === "PENDING" && (
          <div className="flex items-center gap-2">
            <button
              data-testid={`approve-run-${run.id}`}
              onClick={() => onApprove(run.id, notes)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-700/40 hover:bg-teal-700/60 border border-teal-700/40 text-teal-300 text-xs font-medium transition-colors"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
            </button>
            <button
              data-testid={`reject-run-${run.id}`}
              onClick={() => setRejectMode(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-900/30 hover:bg-red-900/50 border border-red-800/40 text-red-400 text-xs font-medium transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Reject
            </button>
          </div>
        )}
        {run.status !== "PENDING" && (
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <Lock className="w-3 h-3" /> Locked
          </span>
        )}

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 text-slate-500 hover:text-slate-300 transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Reject form */}
      {rejectMode && (
        <div className="mx-5 mb-4 p-4 rounded-xl border border-red-800/40 bg-red-950/10 space-y-3">
          <div className="text-sm font-semibold text-red-300">Reject Normalization Run</div>
          <textarea
            data-testid="rejection-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="State the reason for rejection (required)…"
            rows={3}
            className="w-full bg-slate-800 border border-red-800/40 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder:text-slate-600 focus:outline-none resize-none"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setRejectMode(false)} className="text-xs text-slate-400 hover:text-slate-200 transition-colors px-3 py-1.5">Cancel</button>
            <button
              data-testid="confirm-reject-btn"
              onClick={() => { onReject(run.id, reason); setRejectMode(false); }}
              disabled={!reason.trim()}
              className="px-4 py-1.5 rounded-lg bg-red-700 hover:bg-red-600 text-white text-xs font-semibold disabled:opacity-40 transition-colors"
            >
              Confirm Reject
            </button>
          </div>
        </div>
      )}

      {/* Approval notes (for PENDING approval) */}
      {run.status === "PENDING" && !rejectMode && (
        <div className="px-5 pb-3">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional approval notes…"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300 placeholder:text-slate-600 focus:outline-none"
          />
        </div>
      )}

      {/* Decision record */}
      {run.status === "APPROVED" && run.approval_notes && (
        <div className="mx-5 mb-4 text-xs text-teal-500/80 italic">Approval notes: "{run.approval_notes}"</div>
      )}
      {run.status === "REJECTED" && run.approval_notes && (
        <div className="mx-5 mb-4 text-xs text-red-500/80 italic">Rejection reason: "{run.approval_notes}"</div>
      )}

      {/* Expanded: input stats + output stats + population */}
      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-slate-800">
          {/* Stats */}
          <div className="pt-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Input Statistics</div>
            <div className="flex flex-wrap gap-2">
              <StatCard label="Mean" value={inputStats.mean?.toFixed(2)} />
              <StatCard label="Std Dev" value={inputStats.stddev?.toFixed(2)} />
              <StatCard label="Min" value={inputStats.min} />
              <StatCard label="Max" value={inputStats.max} />
              <StatCard label="N" value={inputStats.n} />
            </div>
          </div>
          {run.status === "APPROVED" && Object.keys(outputStats).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Output Statistics</div>
              <div className="flex flex-wrap gap-2">
                <StatCard label="Mean" value={outputStats.mean?.toFixed(2)} />
                <StatCard label="Std Dev" value={outputStats.stddev?.toFixed(2)} />
                <StatCard label="Min" value={outputStats.min} />
                <StatCard label="Max" value={outputStats.max} />
              </div>
            </div>
          )}

          {/* Frozen population */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Frozen Population ({run.members?.length ?? 0} cases) — Immutable Snapshot
            </div>
            <MembersTable members={run.members} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create run modal ─────────────────────────────────────────────────────────

function CreateRunModal({ onClose, onCreate }) {
  const [algo, setAlgo] = useState("Z_SCORE");
  const [scope] = useState("OFFERING");
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    setBusy(true);
    try {
      await onCreate(algo, scope);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div
        data-testid="create-run-modal"
        className="w-full max-w-md mx-4 rounded-2xl border border-slate-700 bg-slate-900 overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <span className="text-sm font-semibold text-slate-200">New Normalization Run</span>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-400 mb-2">Algorithm</div>
            <div className="space-y-2">
              {ALGORITHMS.map((a) => (
                <label
                  key={a.value}
                  data-testid={`algo-option-${a.value}`}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    algo === a.value ? "border-sky-700/60 bg-sky-950/20" : "border-slate-700/40 bg-slate-800/20 hover:bg-slate-800/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="algorithm"
                    value={a.value}
                    checked={algo === a.value}
                    onChange={() => setAlgo(a.value)}
                    className="mt-0.5 accent-sky-500 flex-shrink-0"
                  />
                  <div>
                    <div className={`text-sm font-medium ${algo === a.value ? "text-sky-300" : "text-slate-300"}`}>{a.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{a.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-600 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              The frozen population snapshot is taken at creation time. All cases in{" "}
              {scope === "OFFERING" ? "this offering" : "the selected session"} with a resolved mark
              are included. The snapshot is immutable — it cannot be modified after creation.
            </span>
          </div>
        </div>
        <div className="flex gap-2 justify-end px-6 pb-5">
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200 px-4 py-2 transition-colors">Cancel</button>
          <button
            data-testid="confirm-create-run-btn"
            onClick={handleCreate}
            disabled={busy}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-sky-700 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create Run
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EvaluationNormalization() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(
        `/evaluation/normalization-runs?offering_id=${OFFERING_ID}&component_id=${COMPONENT_ID}`
      );
      setData(res);
    } catch (e) {
      toast.error("Failed to load normalization data: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (algorithm, normalization_scope) => {
    try {
      await apiFetch("/evaluation/normalization-runs", {
        method: "POST",
        body: JSON.stringify({
          offering_id: OFFERING_ID,
          component_id: COMPONENT_ID,
          algorithm,
          normalization_scope,
        }),
      });
      toast.success("Normalization run created.");
      setShowCreate(false);
      await load();
    } catch (e) {
      if (e.status === 409) {
        toast.error("Normalization is disabled in policy, or no resolved cases available.");
      } else {
        toast.error("Failed to create normalization run: " + e.message);
      }
    }
  };

  const handleApprove = async (runId, notes) => {
    try {
      await apiFetch(`/evaluation/normalization-runs/${runId}/approve`, {
        method: "POST",
        body: JSON.stringify({ approval_notes: notes || null }),
      });
      toast.success("Normalization run approved. Canonical marks updated.");
      await load();
    } catch (e) {
      toast.error("Approval failed: " + e.message);
    }
  };

  const handleReject = async (runId, reason) => {
    if (!reason?.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }
    try {
      await apiFetch(`/evaluation/normalization-runs/${runId}/reject`, {
        method: "POST",
        body: JSON.stringify({ rejection_reason: reason }),
      });
      toast.success("Normalization run rejected. Original resolved marks remain canonical.");
      await load();
    } catch (e) {
      toast.error("Rejection failed: " + e.message);
    }
  };

  const normEnabled = data?.normalization_enabled ?? false;
  const runs = data?.runs ?? [];

  return (
    <EvaluationShell title="Normalization" breadcrumb="Normalization">
      {showCreate && (
        <CreateRunModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading normalization data…</span>
        </div>
      ) : !normEnabled ? (
        /* ── DISABLED STATE ── NOT an error, just a policy setting ── */
        <div
          data-testid="normalization-disabled-notice"
          className="flex items-start gap-4 p-6 rounded-2xl border border-slate-700/60 bg-slate-900/40"
        >
          <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-300">Normalization is not enabled for this component</div>
            <div className="text-xs text-slate-500 mt-1 max-w-lg">
              The assessment policy for this offering has{" "}
              <code className="bg-slate-800 px-1 py-0.5 rounded text-slate-400">normalization_enabled = FALSE</code>.
              Normalization is <strong>optional</strong> — this is not an error.
              When normalization is not needed, all resolved marks (from first mark, second mark, or moderation)
              pass directly to the ratification stage.
            </div>
            <div className="mt-3 text-xs text-slate-600">
              To enable normalization, update <code className="bg-slate-800 px-1 rounded text-slate-500">assessment_policy.normalization_enabled</code> to TRUE.
            </div>
          </div>
        </div>
      ) : (
        /* ── ENABLED STATE ── */
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                data-testid="normalization-enabled-badge"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-950 text-teal-300 border border-teal-800/60 text-xs font-semibold"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Normalization Enabled
              </span>
              <span className="text-xs text-slate-500">{runs.length} run{runs.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="refresh-norm-btn"
                onClick={load}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 text-xs hover:border-slate-600 hover:text-slate-200 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
              <button
                data-testid="create-norm-run-btn"
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-600 text-white text-xs font-semibold transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                New Run
              </button>
            </div>
          </div>

          {runs.length === 0 ? (
            <div
              data-testid="empty-norm-runs"
              className="flex flex-col items-center justify-center py-16 text-slate-500"
            >
              <BarChart3 className="w-10 h-10 mb-3 opacity-30" />
              <div className="text-sm font-medium">No normalization runs yet</div>
              <div className="text-xs mt-1 text-slate-600">
                Create a run to normalize the resolved marks for this component.
              </div>
            </div>
          ) : (
            runs.map((run) => (
              <RunCard
                key={run.id}
                run={run}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))
          )}

          <div className="flex items-start gap-2 text-[10px] text-slate-600 bg-slate-900/40 border border-slate-800/60 rounded-lg px-4 py-3">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Normalization is optional and configured at the assessment policy level.
              Approving a run makes the output_marks canonical for each case.
              Rejecting preserves the original resolved_mark.
              All decisions are immutable and form part of the audit trail.
            </span>
          </div>
        </div>
      )}
    </EvaluationShell>
  );
}
